import { useEffect, useRef, useState } from 'react';
import { fetchTestRecordings } from 'api/testing';
import { setConnectionAudioSourceDevice } from 'api/audio-sources';
import { isTestingMode } from 'components/TestingMode';
import { readMicEnabled, readPreferredAudioSourceDeviceId, writeMicEnabled, } from 'lib/audio-source-storage';
export const useAudioSourcesSync = ({ eventId, wsClient, connectionId, transportOnline, }) => {
    const [micEnabled, setMicEnabledState] = useState(readMicEnabled);
    const prevMicEnabledRef = useRef(null);
    const setMicEnabled = (enabled) => {
        writeMicEnabled(enabled);
        setMicEnabledState(enabled);
    };
    useEffect(() => {
        if (!transportOnline || wsClient === null || connectionId === null) {
            return;
        }
        const micJustEnabled = prevMicEnabledRef.current === false && micEnabled;
        prevMicEnabledRef.current = micEnabled;
        if (!micEnabled) {
            wsClient.sendAudioSourcesSync([]);
            return;
        }
        let cancelled = false;
        const syncSources = async () => {
            if (cancelled || wsClient === null || connectionId === null) {
                return;
            }
            let sources = [];
            if (isTestingMode()) {
                const result = await fetchTestRecordings();
                if (result.isSuccess()) {
                    sources = result.get().map((recording) => ({
                        deviceId: recording.id,
                        label: recording.title,
                    }));
                }
            }
            else {
                try {
                    await navigator.mediaDevices.getUserMedia({ audio: true });
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    sources = devices
                        .filter((device) => device.kind === 'audioinput' && device.deviceId !== '')
                        .map((device) => ({
                        deviceId: device.deviceId,
                        label: device.label || device.deviceId,
                    }));
                }
                catch (err) {
                    console.error('Failed to access microphone devices:', err);
                }
            }
            if (!cancelled) {
                wsClient.sendAudioSourcesSync(sources);
                if (micJustEnabled) {
                    const preferred = readPreferredAudioSourceDeviceId();
                    if (preferred !== null
                        && sources.some((source) => source.deviceId === preferred)) {
                        setConnectionAudioSourceDevice(eventId, connectionId, preferred);
                    }
                }
            }
        };
        void syncSources();
        if (!isTestingMode()) {
            navigator.mediaDevices.addEventListener('devicechange', syncSources);
            return () => {
                cancelled = true;
                navigator.mediaDevices.removeEventListener('devicechange', syncSources);
            };
        }
        return () => {
            cancelled = true;
        };
    }, [eventId, micEnabled, transportOnline, wsClient, connectionId]);
    return { micEnabled, setMicEnabled };
};
